import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, UserPlus, Users, Crown, Check, Loader2, Mail } from 'lucide-react';
import DashboardModal from '@/components/dashboard-modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { useUser } from '@/hooks/auth/use-user';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';

interface InviteMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  workbookId?: string;
}

interface SearchResult {
  id: string;
  first_name: string;
  username: string;
  display_name: string;
  profile_picture_url?: string;
}

const InviteMembersModal: React.FC<InviteMembersModalProps> = ({
  isOpen,
  onClose,
  workbookId
}) => {
  const { user } = useUser();
  const { onInviteSuccess } = useGlobalModals();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isInviting, setIsInviting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [projectName, setProjectName] = useState('');
  
  // Email validation regex
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUsers(new Set());
      setHasSearched(false);
      setProjectName('');
    } else if (workbookId) {
      // Fetch project name when modal opens
      fetchProjectName();
    }
  }, [isOpen, workbookId]);
  
  const fetchProjectName = async () => {
    if (!workbookId) return;
    
    const { data, error } = await supabase
      .from('workbook')
      .select('title')
      .eq('id', workbookId)
      .single();
    
    if (data && !error) {
      setProjectName(data.title);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
        setHasSearched(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      // Call the new public_profile_search function
      const { data, error } = await supabase.rpc('public_profile_search', {
        p_search_term: searchQuery.trim(),
        p_limit: 10
      });

      if (error) {
        toast({
          title: "Search failed",
          description: "Failed to search for users. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Filter out current user and format results
      const results = Array.isArray(data) ? data.filter((searchUser: any) => {
        const isCurrentUser = searchUser.id === user?.id;
        
        if (isCurrentUser) {
          return false;
        }
        
        return true;
      }) : [];

      setSearchResults(results);

    } catch (error) {
      toast({
        title: "Search failed",
        description: "Failed to search for users. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleToggleUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };
  
  const handleInviteByEmail = async (email: string) => {
    if (!workbookId || !user) {
      toast({
        title: "Error",
        description: "Missing project or user information",
        variant: "destructive"
      });
      return;
    }
    
    setIsInviting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No active session");
      }
      
      // Get user's first name for the invite
      const { data: userData } = await supabase
        .from('public_profile')
        .select('first_name')
        .eq('user_id', user.id)
        .single();
      
      const response = await supabase.functions.invoke('invite-user-to-project', {
        body: {
          email: email,
          projectId: workbookId,
          projectName: projectName,
          inviterName: userData?.first_name || user.email
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (response.error) {
        throw response.error;
      }
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: response.data.message,
          variant: "default"
        });
        
        // Clear search and close modal
        setSearchQuery('');
        setHasSearched(false);
        
        // Notify parent component to refresh
        if (onInviteSuccess) {
          onInviteSuccess();
        }
        
        onClose();
      } else {
        throw new Error(response.data?.error || "Failed to send invite");
      }
    } catch (error: any) {
      console.error('Failed to invite user by email:', error);
      toast({
        title: "Invitation failed",
        description: error.message || "Failed to send invitation",
        variant: "destructive"
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleInviteMembers = async () => {
    if (!workbookId || selectedUsers.size === 0) {
      console.error('❌ INVITE ERROR: Missing workbookId or no users selected');
      return;
    }

    setIsInviting(true);

    try {
      // Add each selected user as an editor
      const invitePromises = Array.from(selectedUsers).map(async (userId) => {
        
        const { data, error } = await supabase.rpc('workbook_collaboration', {
          p_action: 'add_editor',
          p_workbook_id: workbookId,
          p_user_id: userId
        });

        if (error) {
          console.error('❌ INVITE ERROR: Failed to invite user', { userId, error });
          throw new Error(`Failed to invite user ${userId}: ${error.message}`);
        }

        return { userId, success: true, data };
      });

      // Wait for all invitations to complete
      const results = await Promise.allSettled(invitePromises);
      
      // Process results
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      // Show appropriate toast message
      if (failed === 0) {
        toast({
          title: "Members invited successfully!",
          description: `${successful} member${successful > 1 ? 's' : ''} added to the workbook.`,
          variant: "default"
        });
      } else if (successful > 0) {
        toast({
          title: "Partial success",
          description: `${successful} members invited, ${failed} failed. Please try again for failed invitations.`,
          variant: "default"
        });
      } else {
        toast({
          title: "Invitation failed",
          description: "Failed to invite any members. Please try again.",
          variant: "destructive"
        });
      }

      // Close modal and reset state on any success
      if (successful > 0) {
        setSelectedUsers(new Set());
        setSearchQuery('');
        setSearchResults([]);
        setHasSearched(false);
        
        // Trigger quest completion for team member addition (Step 8)
        QuestTriggers.teamMemberAdded();
        
        // Trigger the success callback before closing
        if (onInviteSuccess) {
          onInviteSuccess();
        }
        
        onClose();
      }

    } catch (error) {
      console.error('❌ INVITE ERROR: Unexpected error during invitation process', error);
      toast({
        title: "Invitation failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsInviting(false);
    }
  };



  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Members"
      size="lg"
      accentColor="purple"
      showCloseButton={true}
    >
      <div className="space-y-6">
        {/* Search Input with Inline Buttons */}
        <div className="space-y-4">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20">
              <Search size={16} className="text-accent" />
            </div>
            <Input
              placeholder="Search by name, username, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 text-sm font-medium bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 hover:border-accent/30"
              disabled={isInviting}
            />
          </div>

          {/* Inline Action Buttons - Only show when users are selected */}
          {selectedUsers.size > 0 && (
            <div className="flex items-center justify-between gap-4 p-4 bg-accent/5 border border-accent/10 rounded-xl">
              {/* Left side - Selection count */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>
                  {selectedUsers.size} member{selectedUsers.size > 1 ? 's' : ''} selected
                </span>
              </div>

              {/* Right side - Action buttons */}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedUsers(new Set())}
                  disabled={isInviting}
                  className="border-border/50 hover:border-border"
                >
                  Clear Selection
                </Button>
                <Button
                  size="sm"
                  onClick={handleInviteMembers}
                  disabled={isInviting || selectedUsers.size === 0}
                  className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inviting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Invite {selectedUsers.size} Member{selectedUsers.size > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Search Results */}
        <div className="min-h-[200px] max-h-[400px] overflow-y-auto">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              <AnimatePresence>
                {searchResults.map((result) => (
                  <motion.div
                    key={result.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={cn(
                      "p-4 rounded-xl border border-accent/10 bg-background/50 hover:bg-accent/5 transition-all duration-200 cursor-pointer",
                      selectedUsers.has(result.id) && "bg-accent/10 border-accent/30"
                    )}
                                                              onClick={() => handleToggleUser(result.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={result.profile_picture_url} alt={result.display_name} />
                          <AvatarFallback className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-600 dark:text-purple-400">
                            {result.first_name?.[0]?.toUpperCase() || result.username?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-sm truncate">
                              {result.first_name || 'Anonymous'}
                            </h3>
                            {result.username && (
                              <Badge variant="secondary" className="text-xs rounded-full">
                                @{result.username}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedUsers.has(result.id) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="p-1 rounded-full bg-green-500/20"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : hasSearched && searchQuery.trim() ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              {isValidEmail(searchQuery.trim()) ? (
                <>
                  <Mail className="h-12 w-12 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground mb-2">No user found with email</p>
                  <p className="text-sm font-medium mb-4">{searchQuery}</p>
                  <p className="text-xs text-muted-foreground/70 mb-4">
                    Would you like to invite them to collaborate?
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleInviteByEmail(searchQuery.trim())}
                    disabled={isInviting}
                    className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
                  >
                    {isInviting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending Invite...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Invite
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Users className="h-12 w-12 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">No users found</p>
                  <p className="text-xs text-muted-foreground/70">
                    Try searching with a different name, username, or email
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">Search for users to invite</p>
              <p className="text-xs text-muted-foreground/70">
                Type at least 2 characters to start searching
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardModal>
  );
};

export default InviteMembersModal; 